import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import { getVideoBySlug, getRelatedVideos } from "@/mocks/videos";

export default function VideoPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const video = useMemo(() => (slug ? getVideoBySlug(slug) : undefined), [slug]);
  const relatedVideos = useMemo(
    () => (video ? getRelatedVideos(video.slug, video.category, 4) : []),
    [video]
  );

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="mt-4 text-foreground-500">视频未找到</p>
            <Link
              to="/"
              className="mt-4 inline-block px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${video.title} - 神马视频高清在线观看`,
    description: video.description,
    image: video.coverImage,
    datePublished: "2024-01-15T08:00:00+08:00",
    dateModified: "2024-06-20T10:30:00+08:00",
    author: {
      "@type": "Organization",
      name: "神马视频",
      url: "https://www.shenmavideo.com",
    },
    publisher: {
      "@type": "Organization",
      name: "神马视频",
      logo: {
        "@type": "ImageObject",
        url: "https://www.shenmavideo.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.shenmavideo.com/video/${video.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: video.categoryName, href: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="lg:w-2/3">
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-900">
                    <img
                      src={video.coverImage}
                      alt={video.title}
                      title={`${video.title} - 神马视频高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-black/60 text-white cursor-pointer hover:bg-primary-500 transition-all duration-300">
                        <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-3 py-1 bg-accent-500 text-background-50 text-sm font-medium rounded">
                        {video.rating} 分
                      </span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading">
                      {video.title}
                    </h1>

                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                      <span className="whitespace-nowrap">
                        <i className="ri-eye-line mr-1"></i>
                        {video.views}次播放
                      </span>
                      <span className="whitespace-nowrap">
                        <i className="ri-folder-line mr-1"></i>
                        {video.categoryName}
                      </span>
                      <span className="whitespace-nowrap">
                        <i className="ri-time-line mr-1"></i>
                        {video.episodeCount}
                      </span>
                      <span className={`inline-block px-2 py-0.5 text-xs rounded whitespace-nowrap ${
                        video.updateStatus === "已完结" || video.updateStatus.includes("HD")
                          ? "bg-green-100 text-green-700"
                          : "bg-accent-100 text-accent-700"
                      }`}>
                        {video.updateStatus}
                      </span>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {video.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 p-5 bg-background-100 rounded-lg">
                    <h2 className="text-base font-semibold text-foreground-900 font-heading mb-3">
                      内容简介
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {video.description}
                    </p>
                  </div>
                </div>

                <aside className="lg:w-1/3">
                  <div className="bg-background-100 rounded-lg p-4 md:p-5">
                    <h3 className="text-base font-semibold text-foreground-900 font-heading mb-4">
                      相关推荐
                    </h3>
                    <div className="space-y-4">
                      {relatedVideos.map((v) => (
                        <Link
                          key={v.id}
                          to={`/video/${v.slug}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-24 h-16 md:w-28 md:h-18 flex-shrink-0 rounded overflow-hidden bg-background-200">
                            <img
                              src={v.coverImage}
                              alt={v.title}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 font-heading">
                              {v.title}
                            </h4>
                            <div className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                              <span className="whitespace-nowrap">{v.categoryName}</span>
                              <span>{v.rating}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 bg-background-100 rounded-lg p-4 md:p-5">
                    <h3 className="text-base font-semibold text-foreground-900 font-heading mb-3">
                      热门标签
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {["高分", "国产", "科幻", "古装", "悬疑", "治愈", "经典", "热剧", "动漫", "综艺"].map((tag) => (
                        <span
                          key={tag}
                          className="inline-block px-3 py-1 bg-background-50 border border-background-200 text-foreground-600 text-xs rounded-full whitespace-nowrap cursor-pointer hover:border-primary-200 hover:text-primary-500 transition-colors"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}